import { useEffect, useRef } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAppBarStatus } from '../components/layout/AppShell'
import { ErrorState, RouteLoading } from '../components/ui/states'
import { Notice } from '../components/ui/Notice'
import { useDocumentTitle, useMediaQuery } from '../lib/dom'
import { formatTime } from '../lib/format'
import { Stepper, type StepperItem, type StepStatus } from './Stepper'
import { WIZARD_STEPS, stepBySlug } from './steps'
import { useWizard, WizardProvider } from './WizardContext'
import './WizardShell.css'

function SaveIndicator() {
  const { saveState, lastSavedAt } = useWizard()
  const time = formatTime(lastSavedAt)

  // Never a spinner. The text simply changes — nothing in this product should
  // imply the user is being made to wait.
  const label =
    saveState === 'error'
      ? 'Not saved'
      : saveState === 'saving'
        ? 'Saving'
        : time
          ? `Saved ${time}`
          : 'Draft started'

  useAppBarStatus(label, [label])
  return null
}

function WizardBody() {
  const wizard = useWizard()
  const location = useLocation()
  const wide = useMediaQuery('(min-width: 64rem)')
  const headingRef = useRef<HTMLHeadingElement>(null)

  const slug = location.pathname.split('/')[2]
  const step = stepBySlug(slug)

  // Autosave also fires on step change, not just on the 800ms debounce.
  useEffect(() => {
    return () => {
      void wizard.saveNow()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  // Focus the heading on step change. A live-region announcement alone is
  // transient: it is gone the instant it fires, leaving a screen-reader user
  // in an unlabelled form with no way to re-check which step they are on.
  // Moving focus to a real <h1> announces the same string *and* leaves
  // something they can navigate back to — so the announce() call this
  // replaced would now be a duplicate.
  // Depends on `isLoading` as well as the step index. On a cold load the body
  // renders a spinner first, so the heading does not exist yet when the effect
  // runs; without the second dependency it never re-fires and focus stays on
  // <body>.
  useEffect(() => {
    if (!step || wizard.isLoading) return
    headingRef.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step?.index, wizard.isLoading])

  useDocumentTitle(step ? `${step.heading} — step ${step.index} of ${WIZARD_STEPS.length}` : 'Wizard')

  if (wizard.isLoading) return <RouteLoading label="Loading your draft" />

  if (wizard.loadError) {
    return (
      <div className="container container--prose">
        <ErrorState
          error={wizard.loadError}
          title="We couldn't open your draft"
          onRetry={wizard.refetchDraft}
        />
      </div>
    )
  }

  // Bare /wizard resolves to the furthest incomplete step, so "continue where
  // you left off" lands where the user actually stopped.
  if (!step) {
    const target = !wizard.validation?.step_1_complete
      ? '/wizard/personal'
      : !wizard.validation.step_2_complete
        ? '/wizard/representatives'
        : '/wizard/review'
    return <Navigate to={target} replace />
  }

  const submitted = wizard.submission !== null
  const draftStep = wizard.validation?.step_2_complete ? 3 : wizard.validation?.step_1_complete ? 2 : 1
  const furthest = submitted ? 4 : Math.max(step.index, draftStep)

  const items: StepperItem[] = WIZARD_STEPS.map((entry) => {
    let status: StepStatus = 'Not started'
    if (entry.index === step.index) status = 'In progress'
    else if (entry.index === 1 && wizard.validation?.step_1_complete) status = 'Complete'
    else if (entry.index === 2 && wizard.validation?.step_2_complete) status = 'Complete'
    else if (entry.index === 3 && submitted) status = 'Complete'
    else if (entry.index === 4 && submitted) status = 'Complete'

    return {
      index: entry.index,
      label: entry.label,
      path: entry.path,
      status,
      // Everything up to the furthest point reached stays a real link, so
      // going back is always one keystroke away.
      reachable: entry.index === 4 ? submitted : entry.index <= furthest,
    }
  })

  return (
    <div className="container">
      <div className="wizard">
        <div className="wizard__rail">
          <Stepper items={items} rail={!wide} />
        </div>

        <div className={step.index === 3 ? 'wizard__main wizard__main--wide' : 'wizard__main'}>
          {wizard.conflict || wizard.saveState === 'error' ? (
            <div className="wizard__notices">
              {wizard.conflict ? (
                <Notice
                  tone="warning"
                  role="alert"
                  title="This form was changed in another window"
                >
                  <p>
                    We have not overwritten it. Reload to pick up the newer version — anything you
                    typed here since will need re-entering.
                  </p>
                  <p>
                    <button className="btn btn--secondary btn--sm" type="button" onClick={wizard.refetchDraft}>
                      Reload the latest version
                    </button>
                  </p>
                </Notice>
              ) : (
                <Notice tone="warning" role="alert" title="We couldn't save just now">
                  <p>We&rsquo;ll keep trying — don&rsquo;t close this tab.</p>
                </Notice>
              )}
            </div>
          ) : null}

          <h1 className="page-header__title wizard__heading" ref={headingRef} tabIndex={-1}>
            {step.heading}
            <span className="u-visually-hidden">
              , step {step.index} of {WIZARD_STEPS.length}
            </span>
          </h1>

          <Outlet />
        </div>
      </div>
    </div>
  )
}

export function WizardShell() {
  return (
    <WizardProvider>
      <SaveIndicator />
      <WizardBody />
    </WizardProvider>
  )
}
